"use client";

/**
 * G3-8b — "Talk to us first" setup-service lead form (£249 reposition,
 * Option A).
 *
 * Secondary, sales-assisted path that sits ALONGSIDE the self-serve £249
 * Checkout CTA on /setup-for-you. It is the client surface for the public,
 * unauthenticated POST /api/leads/setup-service route. The £249 self-serve
 * Stripe flow is untouched.
 *
 * Cloned from components/site-control/site-control-lead-form.tsx (the proven
 * anti-abuse form pattern): honeypot, Turnstile + reset-on-submit, GDPR consent
 * gate, browser attribution, field-error mapping, sonner toasts, terminal
 * success state. Fields adapted: required full name + work email; optional
 * phone, company, and a "what do you need help with" textarea.
 *
 * Copy conveys a prompt response without a hard SLA (founder-confirmed
 * 2026-06-19: we reply as soon as a lead lands).
 *
 * All colours are design tokens, never hardcoded hex (the hex guardrail covers
 * components/).
 */

import { useRef, useState } from "react";
import Link from "next/link";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  TurnstileWidget,
  readTurnstileToken,
  resetTurnstile,
  resolveTurnstileSiteKey,
} from "@/components/ui/turnstile-widget";
import { readBrowserAttributionWithCurrentUrl } from "@/components/analytics/browser-attribution";
import {
  trackSetupServiceEvent,
  deriveDeviceType,
} from "@/lib/setup-service-analytics";

type FieldName = "email" | "fullName" | "phone" | "company" | "message" | "consent";

type FieldErrors = Partial<Record<FieldName, string>>;

// Mirror of the route's z.string().email() — UX-only, server is truth.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SetupServiceLeadForm() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  // Turnstile tokens are single-use — keep a ref so every submit that posted a
  // token can reset the widget for the retry.
  const turnstileRef = useRef<HTMLDivElement | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrors({});

    const form = e.currentTarget;
    const formData = new FormData(form);

    const email = String(formData.get("email") ?? "").trim();
    const fullName = String(formData.get("fullName") ?? "").trim();
    const phone = String(formData.get("phone") ?? "").trim();
    const company = String(formData.get("company") ?? "").trim();
    const message = String(formData.get("message") ?? "").trim();
    // Honeypot — humans never fill this; included verbatim in the POST body.
    const website = String(formData.get("website") ?? "");

    // Cloudflare injects `cf-turnstile-response` inside the form once the
    // widget solves; with no site key the widget renders a manual
    // `turnstileToken` input instead. Read both — an empty token is rejected
    // server-side regardless of environment.
    const turnstileToken = readTurnstileToken(form);

    // Client-side mirror of the server schema (UX-only).
    const localErrors: FieldErrors = {};
    if (!email || !EMAIL_REGEX.test(email)) {
      localErrors.email = "Enter a valid work email";
    }
    if (!fullName) localErrors.fullName = "Required";
    if (!consent) {
      localErrors.consent = "Please accept the privacy notice to continue";
    }
    if (Object.keys(localErrors).length > 0) {
      setErrors(localErrors);
      return;
    }

    // When a site key is configured the widget MUST be solved before we post —
    // otherwise the server 400s with a Turnstile error that maps to no field.
    // Without a key the widget renders nothing and the server verify falls open
    // in non-prod, so an empty token is fine there.
    if (resolveTurnstileSiteKey() && !turnstileToken) {
      toast.error("Please complete the verification check before submitting.");
      return;
    }

    // Build the POST body in the EXACT shape the route's Zod schema expects.
    // Optional fields are omitted when blank so they never become empty strings
    // the server has to special-case.
    const payload: Record<string, unknown> = {
      email,
      fullName,
      consent: true, // only sent when the box is ticked (guarded above)
      turnstileToken,
      website, // honeypot — empty for humans
      // Paid-acquisition attribution passthrough — the route extracts utm/source
      // for the Slack field + PostHog props (it does not persist).
      attribution: readBrowserAttributionWithCurrentUrl(),
    };
    if (phone) payload.phone = phone;
    if (company) payload.company = company;
    if (message) payload.message = message;

    setSubmitting(true);
    try {
      const res = await fetch("/api/leads/setup-service", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        setSubmitted(true);
        toast.success("Thanks — we've got your details and we'll be in touch shortly.");
        // Client analytics for the lead-capture path (GA4 + PostHog).
        trackSetupServiceEvent("setup_service_lead_submitted", {
          source_page: "setup_for_you",
          device_type: deriveDeviceType(),
          cta_label: "talk_to_us",
        });
        return;
      }

      if (res.status === 429) {
        toast.error("Too many requests. Please wait a moment and try again.");
        setSubmitting(false);
        return;
      }

      // 400 (validation) — try to map field errors.
      const data = (await res.json().catch(() => ({}))) as {
        error?: { fieldErrors?: Record<string, string[]> } | string;
      };
      // The server's Turnstile rejection maps to no field — the posted token was
      // spent/expired. The `finally` reset below issues a fresh one; retry.
      if (
        res.status === 400 &&
        typeof data?.error === "string" &&
        data.error.toLowerCase().includes("turnstile")
      ) {
        toast.error("Verification expired — please try again.");
        setSubmitting(false);
        return;
      }
      if (
        res.status === 400 &&
        data?.error &&
        typeof data.error === "object" &&
        data.error.fieldErrors
      ) {
        const fe = data.error.fieldErrors;
        const mapped: FieldErrors = {};
        for (const k of Object.keys(fe) as FieldName[]) {
          const first = fe[k]?.[0];
          if (first) mapped[k] = first;
        }
        setErrors(mapped);
        toast.error("Please check the highlighted fields and try again.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
      setSubmitting(false);
    } catch {
      toast.error("Couldn't reach the server. Please try again.");
      setSubmitting(false);
    } finally {
      // The token we just posted is spent regardless of outcome — reset the
      // widget so a retry submits a fresh token.
      resetTurnstile(turnstileRef.current);
    }
  };

  const inputBaseClass =
    "w-full rounded-lg border border-border px-3 py-3 text-base md:text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent min-h-[48px]";

  const fieldClass = (field: FieldName) =>
    errors[field]
      ? `${inputBaseClass} border-danger focus:ring-danger`
      : inputBaseClass;

  // Terminal success state — replaces the form so there's no double submit.
  if (submitted) {
    return (
      <div className="mx-auto w-full max-w-md rounded-2xl border border-done-line bg-done-bg p-8 text-center">
        <CheckCircle2
          className="mx-auto h-10 w-10 text-done"
          aria-hidden="true"
        />
        <h3 className="mt-4 font-heading text-xl font-bold text-navy">
          Thanks — we&apos;ve got your details
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-muted">
          We&apos;ll be in touch shortly to talk through your job and how we can
          help you get set up.
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="mx-auto w-full max-w-md space-y-5"
    >
      {/* Honeypot — visually hidden, off the tab order, ignored by AT.
          Humans never fill it; bots that fill every input trip the server. */}
      <div aria-hidden="true" className="sr-only">
        <label htmlFor="ssl-website">Leave this field empty</label>
        <input
          id="ssl-website"
          name="website"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          defaultValue=""
        />
      </div>

      {/* Full name */}
      <div>
        <label
          htmlFor="ssl-fullName"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Full name <span className="text-danger">*</span>
        </label>
        <input
          id="ssl-fullName"
          name="fullName"
          type="text"
          required
          autoComplete="name"
          placeholder="Your name"
          aria-invalid={!!errors.fullName}
          aria-describedby={errors.fullName ? "ssl-fullName-error" : undefined}
          className={fieldClass("fullName")}
        />
        {errors.fullName ? (
          <p id="ssl-fullName-error" className="mt-1 text-xs text-danger">
            {errors.fullName}
          </p>
        ) : null}
      </div>

      {/* Work email */}
      <div>
        <label
          htmlFor="ssl-email"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Work email <span className="text-danger">*</span>
        </label>
        <input
          id="ssl-email"
          name="email"
          type="email"
          required
          autoComplete="email"
          placeholder="you@yourcompany.co.uk"
          aria-invalid={!!errors.email}
          aria-describedby={errors.email ? "ssl-email-error" : undefined}
          className={fieldClass("email")}
        />
        {errors.email ? (
          <p id="ssl-email-error" className="mt-1 text-xs text-danger">
            {errors.email}
          </p>
        ) : null}
      </div>

      {/* Phone (optional) */}
      <div>
        <label
          htmlFor="ssl-phone"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Phone{" "}
          <span className="text-text-tertiary font-normal">(optional)</span>
        </label>
        <input
          id="ssl-phone"
          name="phone"
          type="tel"
          maxLength={50}
          autoComplete="tel"
          placeholder="07xxx xxx xxx"
          className={inputBaseClass}
        />
      </div>

      {/* Company (optional) */}
      <div>
        <label
          htmlFor="ssl-company"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          Company{" "}
          <span className="text-text-tertiary font-normal">(optional)</span>
        </label>
        <input
          id="ssl-company"
          name="company"
          type="text"
          maxLength={200}
          autoComplete="organization"
          placeholder="Your construction firm"
          className={inputBaseClass}
        />
      </div>

      {/* What do you need help with? (optional) */}
      <div>
        <label
          htmlFor="ssl-message"
          className="mb-1 block text-sm font-medium text-foreground"
        >
          What do you need help with?{" "}
          <span className="text-text-tertiary font-normal">(optional)</span>
        </label>
        <textarea
          id="ssl-message"
          name="message"
          rows={4}
          maxLength={2000}
          placeholder="Tell us about the job, the deadline, and what the PC or client has asked for."
          className={`${inputBaseClass} min-h-[96px] resize-y`}
        />
      </div>

      {/* Cloudflare Turnstile — anti-bot. Renders nothing without a site key. */}
      <TurnstileWidget className="mt-2" widgetRef={turnstileRef} />

      {/* GDPR consent — UNTICKED by default; submit is blocked until ticked. */}
      <div>
        <label
          htmlFor="ssl-consent"
          className="flex items-start gap-3 text-sm text-muted"
        >
          <input
            id="ssl-consent"
            name="consent"
            type="checkbox"
            checked={consent}
            onChange={(e) => {
              setConsent(e.target.checked);
              if (e.target.checked) {
                setErrors((prev) => ({ ...prev, consent: undefined }));
              }
            }}
            aria-invalid={!!errors.consent}
            aria-describedby={errors.consent ? "ssl-consent-error" : undefined}
            className="mt-0.5 h-5 w-5 shrink-0 rounded border-border text-accent focus:ring-2 focus:ring-accent"
          />
          <span>
            I agree to be contacted about the setup service and have read the{" "}
            <Link
              href="/legal/privacy"
              className="font-medium text-brand-700 underline hover:text-accent-dark"
            >
              privacy notice
            </Link>
            .
          </span>
        </label>
        {errors.consent ? (
          <p id="ssl-consent-error" className="mt-1 text-xs text-danger">
            {errors.consent}
          </p>
        ) : null}
      </div>

      {/* Submit — disabled until consent is ticked or while in-flight. */}
      <button
        type="submit"
        disabled={!consent || submitting}
        className="flex w-full min-h-[48px] items-center justify-center gap-2 rounded-lg bg-accent px-4 py-3 text-base font-semibold text-white transition-colors hover:bg-accent-dark disabled:cursor-not-allowed disabled:opacity-50"
      >
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Sending…
          </>
        ) : (
          "Talk to us"
        )}
      </button>
    </form>
  );
}
